
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Edit, Loader2, Plus, Trash } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

interface Tag {
  id: number;
  name: string;
  slug: string;
}

const formSchema = z.object({
  name: z.string().min(1, { message: 'Tag name cannot be empty' }),
  slug: z.string().min(1, { message: 'URL slug cannot be empty' }).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: 'URL slug can only contain lowercase letters, numbers and hyphens',
  }),
});

type FormValues = z.infer<typeof formSchema>;

const AdminTags: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      slug: '',
    },
  });

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cms_tags')
        .select('*')
        .order('name');

      if (error) throw error;
      setTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to get tags',
        description: 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      if (editingTag) {
        // Update existing tag
        const { error } = await supabase
          .from('cms_tags')
          .update({
            name: values.name,
            slug: values.slug,
          })
          .eq('id', editingTag.id);

        if (error) throw error;

        toast({
          title: 'Update successful',
          description: 'Tag has been updated',
        });
      } else {
        // Create new tag
        const { error } = await supabase
          .from('cms_tags')
          .insert({
            name: values.name,
            slug: values.slug,
          });

        if (error) throw error;

        toast({
          title: 'Creation successful',
          description: 'Tag has been created',
        });
      }

      // Reset form and refresh tags
      setEditingTag(null);
      setDialogOpen(false);
      form.reset({
        name: '',
        slug: '',
      });
      fetchTags();
    } catch (error) {
      console.error('Error saving tag:', error);
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: 'An error occurred while saving the tag, please try again later',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTag = (tag: Tag) => {
    setEditingTag(tag);
    form.reset({
      name: tag.name,
      slug: tag.slug,
    });
    setDialogOpen(true);
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;
    
    try {
      // Check if there are posts using this tag
      const { count, error: countError } = await supabase
        .from('cms_post_tags')
        .select('*', { count: 'exact', head: true })
        .eq('tag_id', tagToDelete.id);

      if (countError) throw countError;

      if (count && count > 0) {
        toast({
          variant: 'destructive',
          title: 'Cannot delete',
          description: `This tag is used by ${count} posts, please remove the associations first`,
        });
        setDeleteDialogOpen(false);
        setTagToDelete(null);
        return;
      }

      const { error } = await supabase
        .from('cms_tags')
        .delete()
        .eq('id', tagToDelete.id);

      if (error) throw error;

      toast({
        title: 'Delete successful',
        description: 'Tag has been deleted',
      });

      // Update the local state
      setTags(tags.filter(tag => tag.id !== tagToDelete.id));
      setDeleteDialogOpen(false);
      setTagToDelete(null);
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: 'Cannot delete the tag',
      });
    }
  };

  const handleAddNew = () => {
    setEditingTag(null);
    form.reset({
      name: '',
      slug: '',
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingTag(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Tag Management</h2>
        <Button 
          onClick={handleAddNew}
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Tag
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Tag List</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>URL Slug</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tags.length > 0 ? (
                    tags.map((tag) => (
                      <TableRow key={tag.id}>
                        <TableCell>{tag.id}</TableCell>
                        <TableCell className="font-medium">{tag.name}</TableCell>
                        <TableCell className="font-mono text-sm">{tag.slug}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditTag(tag)}
                            >
                              <Edit className="h-3.5 w-3.5 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => {
                                setTagToDelete(tag);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash className="h-3.5 w-3.5 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center">
                        No tag data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
            <DialogDescription>
              {editingTag 
                ? 'Modify tag information and save changes' 
                : 'Fill in tag details to create a new tag'}
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tag Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter tag name" 
                        {...field} 
                        disabled={submitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>URL Slug</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="spiritual-healing" 
                        {...field} 
                        disabled={submitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleDialogClose}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the tag "{tagToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteTag}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTags;
